import { useEffect, useMemo } from "react";
import { useLocation, Link } from "react-router-dom";
import { ArrowLeft, Mail } from "lucide-react";

const LAST_UPDATED = "February 12, 2026";
const CONTACT = "hello@blendsafe.com";
const BRAND = "BlendSafe";
const DOMAIN = "blendsafe.com";

const PAGES = {
  privacy: {
    title: "Privacy Policy",
    eyebrow: "How we handle your data",
    intro: (
      <>
        BlendSafe is a pre-launch consumer health-tech product. We collect the
        minimum amount of information needed to run the safety checker, the
        Founding Member waitlist, and basic product analytics — and we never
        sell your data to third parties.
      </>
    ),
    sections: [
      {
        h: "What we collect",
        p: [
          "When you run a safety check we record the medication and product names so we can deliver a verdict, save the result for trend analytics, and improve the interaction database. Anonymous checks are stored without an account identifier.",
          "When you join the Founding Member waitlist we collect your email, IP address, country, and the marketing source that brought you to BlendSafe. We use this to send launch updates, prioritize tiered access, and combat duplicate signups.",
          "We use first-party analytics to track aggregate page views, response time percentiles, and error counts. We do not use third-party advertising or behavioural tracking on the marketing site.",
        ],
      },
      {
        h: "How we use it",
        p: [
          "To run safety checks, generate verdicts, and surface trending combinations.",
          "To email you launch and product updates when you've joined the waitlist (you can unsubscribe at any time).",
          "To monitor service performance, fix bugs, and improve the interaction engine.",
          "To comply with legal obligations and to detect fraud or abuse.",
        ],
      },
      {
        h: "What we never do",
        p: [
          "We never sell, rent, or trade your personal information.",
          "We never use your data to train third-party advertising profiles.",
          "We never expose your individual check history publicly. Aggregate trending data on the homepage is grouped and anonymous.",
        ],
      },
      {
        h: "Your rights",
        p: [
          "You can request a copy of any data we hold about you, ask us to correct it, or ask us to delete it. Email us at the address below and we'll respond within 30 days.",
          "If you join the waitlist and later change your mind, you can unsubscribe from the link inside any email we send, or email us directly.",
        ],
      },
      {
        h: "Data location & retention",
        p: [
          "Data is stored on US-region cloud infrastructure. Anonymous check results are retained indefinitely to keep the trending and 'Also worth checking' surfaces accurate. Waitlist records are retained until you unsubscribe or request deletion.",
        ],
      },
    ],
  },
  terms: {
    title: "Terms of Use",
    eyebrow: "The agreement between you and BlendSafe",
    intro: (
      <>
        BlendSafe is an informational tool — not a medical device, not a
        prescription, and not a substitute for professional medical advice. By
        using BlendSafe you agree to the terms below.
      </>
    ),
    sections: [
      {
        h: "What BlendSafe is",
        p: [
          "BlendSafe checks skincare ingredients against common prescription medications using an editorial interaction database. Verdicts (Avoid / Use with caution / No label conflict found / Not enough verified data) are informational guidance and are not personalized medical advice.",
          "Always consult a licensed dermatologist or your prescribing physician before changing a treatment, layering a new active, or stopping a medication.",
        ],
      },
      {
        h: "Acceptable use",
        p: [
          "Use BlendSafe for personal, non-commercial skincare research.",
          "Do not scrape the safety checker, the OG image endpoint, or the interaction database. Reasonable rate limits apply.",
          "Do not impersonate a healthcare provider when sharing results.",
        ],
      },
      {
        h: "Free and Premium",
        p: [
          "Free safety checking stays open at launch. Premium adds memory, continuity, personalized skin logic, continuous monitoring, dermatologist-ready PDF export, and extended scanning access.",
          "Founding Member status (when available) stays on your account permanently as long as the account remains in good standing.",
          "Premium subscription pricing, billing, and cancellation policies will be presented at the point of sale. Cancel anytime — no lock-ins.",
        ],
      },
      {
        h: "No medical advice",
        p: [
          "BlendSafe does not diagnose, treat, cure, or prevent any disease. We do not store prescription details from your pharmacy. The interaction engine is a reference — the licensed clinician who wrote your prescription remains the source of truth for your individual situation.",
        ],
      },
      {
        h: "Limitation of liability",
        p: [
          "BlendSafe is provided 'as is' without warranties of any kind. To the maximum extent permitted by law, BlendSafe is not liable for any indirect, consequential, or incidental damages arising from your use of the service.",
        ],
      },
    ],
  },
  disclaimer: {
    title: "Medical Disclaimer",
    eyebrow: "BlendSafe is not a substitute for medical advice",
    intro: (
      <>
        BlendSafe is an editorial safety checker designed to help informed
        consumers research skincare-and-medication interactions. It is not a
        regulated medical device.
      </>
    ),
    sections: [
      {
        h: "Always check with a clinician",
        p: [
          "Verdicts on BlendSafe are informational. Skin, medication tolerance, and individual physiology vary widely — a 'No label conflict found' verdict on BlendSafe is not a guarantee that a combination is safe for you, and an 'AVOID' verdict is not a diagnosis of harm.",
          "If you have a skin reaction, an unexpected side effect, or a medication concern, contact your prescribing physician, dermatologist, or pharmacist immediately.",
        ],
      },
      {
        h: "Why the verdicts can change",
        p: [
          "The interaction database evolves as new dermatology research, FDA-approved labels, and clinical reports become available. A combination that scored SAFE today may shift to CAUTION as new evidence appears. That's a feature, not a bug — but it's why we recommend a clinician-led conversation before making routine changes.",
        ],
      },
      {
        h: "Emergencies",
        p: [
          "If you are experiencing a serious adverse reaction, call your local emergency number immediately (911 in the United States). BlendSafe is not an emergency service.",
        ],
      },
    ],
  },
  cookies: {
    title: "Cookie Policy",
    eyebrow: "How and why we use browser storage",
    intro: (
      <>
        BlendSafe uses a small set of first-party cookies and localStorage
        entries to keep the experience smooth. We do not use third-party
        advertising or cross-site tracking cookies on the marketing site.
      </>
    ),
    sections: [
      {
        h: "What we store",
        p: [
          "Recent checks (last 5) — stored in localStorage under `blendsafe_recent_checks_v1` so the homepage can offer one-tap re-runs.",
          "Daily-visit streak — stored in localStorage under `blendsafe_streak_v1` so we can show your streak badge.",
          "Announcement bar dismissal — stored under `blendsafe_announcement_dismissed_v1`.",
          "Founding moment dialog suppression — stored under `blendsafe_founding_moment_seen_v1` (per tab) and `blendsafe_founding_moment_later` (permanent if you click Maybe later).",
          "Authentication tokens (admin only) — stored under `medskin_admin_token` during admin sessions.",
        ],
      },
      {
        h: "What we don't store",
        p: [
          "We do not place third-party advertising cookies on the marketing site.",
          "We do not use behavioural retargeting, cross-site fingerprinting, or any analytics that share your data with ad networks.",
        ],
      },
      {
        h: "How to clear",
        p: [
          "You can clear all BlendSafe browser storage anytime via your browser's site-data settings. Doing so resets your recent-checks list, daily streak, and any dismissed announcements.",
        ],
      },
    ],
  },
};

/**
 * Legal — single component that renders the four launch-required legal
 * pages (Privacy, Terms, Disclaimer, Cookies). Calm cream layout that
 * matches the rest of BlendSafe — premium serif/sans pairing, generous
 * spacing, no marketing CTAs to distract from the content.
 */
export default function Legal() {
  const { pathname } = useLocation();
  const slug = useMemo(() => pathname.replace(/^\/+|\/+$/g, "").toLowerCase(), [pathname]);
  const data = useMemo(() => PAGES[slug] || PAGES.privacy, [slug]);

  // Reset scroll when navigating between legal pages so users land at top.
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
    // Update page title for SEO + share previews of the legal pages.
    document.title = `${data.title} — ${BRAND}`;
  }, [data]);

  return (
    <main data-testid={`legal-${slug}`} className="bg-cream-50 min-h-screen pb-24">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 md:px-8 pt-10 sm:pt-12">
        <Link
          to="/"
          data-testid="legal-back-link"
          className="inline-flex items-center gap-2 text-[13px] text-ink-mute hover:text-ink transition-colors mb-8"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to {DOMAIN}
        </Link>

        <header className="mb-8 sm:mb-10">
          <p className="text-[10px] sm:text-[11px] uppercase tracking-[0.28em] text-ink-mute font-semibold mb-3">
            {data.eyebrow}
          </p>
          <h1
            data-testid="legal-heading"
            className="text-[32px] sm:text-[40px] md:text-[48px] font-extrabold tracking-[-0.025em] leading-[1.05] text-ink"
          >
            {data.title}
          </h1>
          <p className="mt-4 text-[15px] sm:text-[16px] text-ink-soft leading-relaxed">
            {data.intro}
          </p>
          <p className="mt-4 text-[12px] text-ink-mute">
            Last updated {LAST_UPDATED}
          </p>
        </header>

        <article className="space-y-8 text-[14.5px] sm:text-[15px] text-ink-soft leading-relaxed">
          {data.sections.map((s, idx) => (
            <section key={s.h} data-testid={`legal-section-${idx}`}>
              <h2 className="text-[18px] sm:text-[20px] font-extrabold text-ink tracking-[-0.012em] mb-3">
                {s.h}
              </h2>
              <div className="space-y-3">
                {s.p.map((para, i) => (
                  <p key={i}>{para}</p>
                ))}
              </div>
            </section>
          ))}

          <section
            data-testid="legal-contact"
            className="mt-10 rounded-2xl border border-surface-line bg-white px-5 py-5 sm:px-6 sm:py-6"
          >
            <div className="flex items-center gap-3">
              <span
                className="inline-flex items-center justify-center w-10 h-10 rounded-2xl"
                style={{ background: "rgba(168,122,50,0.10)", color: "#a87a32" }}
              >
                <Mail className="w-5 h-5" />
              </span>
              <div>
                <p className="text-[10.5px] uppercase tracking-[0.22em] font-bold text-ink-mute">
                  Questions?
                </p>
                <a
                  href={`mailto:${CONTACT}`}
                  className="block text-[15px] sm:text-[16px] font-semibold text-ink hover:underline"
                  style={{ textDecorationColor: "#a87a32" }}
                >
                  {CONTACT}
                </a>
              </div>
            </div>
          </section>
        </article>
      </div>
    </main>
  );
}
